import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import CurrencyDisplay from "../common/CurrencyDisplay";
// currency utilities
import { useCurrencyUtils } from "../../utils/currencyUtils";
import api from "../../lib/api.ts";

// Helper function to convert Tailwind color names to hex values
const getColorHex = (color) => {
  const colorMap = {
    blue: "#2563eb",
    green: "#16a34a",
    purple: "#9333ea",
    orange: "#ea580c",
    red: "#dc2626",
    gray: "#6b7280",
    indigo: "#4f46e5",
    pink: "#db2777",
    teal: "#0d9488",
  };
  return colorMap[color] || "#000000";
};

const Sales = () => {
  const { user } = useAuth();
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const { convertPrice, displayPriceWithSymbol } = useCurrencyUtils();
  const [isLoading, setIsLoading] = useState(true);
  const [apiData, setApiData] = useState({});

  useEffect(() => {
    const fetchInvoices = async () => {
      const response = await api.get(
        `/sellerwithdrawalinvoices/paginated?orderBy=id`
      );
      
    };
    fetchInvoices();
  }, []);

  // Fetch sales data from API
  useEffect(() => {
    const fetchSalesData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(
          `/stats/seller/${user.seller.id}/totalcounts`
        );
        setApiData(response.data);
      } catch (error) {
        console.error("Error fetching sales data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalesData();
  }, [user]);

  // Stat Card Component
  const StatCard = ({
    title,
    value,
    icon,
    color,
    isAmount = false,
    showWithdrawButton = false,
  }) => {
    const handleWithdraw = () => {
      console.log("Withdraw button clicked");
    };

    // Color config for mobile accent styling
    const colorConfig = {
      blue:   { bar: "bg-blue-500",   icon: "bg-blue-100",   label: "text-blue-600",   value: "text-blue-700" },
      green:  { bar: "bg-green-500",  icon: "bg-green-100",  label: "text-green-600",  value: "text-green-700" },
      orange: { bar: "bg-orange-500", icon: "bg-orange-100", label: "text-orange-600", value: "text-orange-700" },
      red:    { bar: "bg-red-500",    icon: "bg-red-100",    label: "text-red-600",    value: "text-red-700" },
      yellow: { bar: "bg-yellow-500", icon: "bg-yellow-100", label: "text-yellow-600", value: "text-yellow-800" },
      pink:   { bar: "bg-pink-500",   icon: "bg-pink-100",   label: "text-pink-600",   value: "text-pink-700" },
      teal:   { bar: "bg-teal-500",   icon: "bg-teal-100",   label: "text-teal-600",   value: "text-teal-700" },
      indigo: { bar: "bg-indigo-500", icon: "bg-indigo-100", label: "text-indigo-600", value: "text-indigo-700" },
    };
    const cfg = colorConfig[color] || colorConfig.blue;

    return (
      <>
        {/* ── Mobile Card (< sm) ── */}
        <div className={`sm:hidden relative overflow-hidden rounded-2xl bg-white shadow-md border border-gray-100 flex flex-col min-h-[110px]`}>
          {/* Left accent bar */}
          <div className={`absolute left-0 top-0 h-full w-1 ${cfg.bar} rounded-l-2xl`} />

          <div className="flex flex-col flex-1 pl-4 pr-3 pt-3 pb-3 gap-2">
            {/* Icon pill + label row */}
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${cfg.icon} flex-shrink-0`}>
                {icon}
              </div>
              <p className={`text-[10px] font-bold uppercase tracking-wider ${cfg.label} leading-tight`}>
                {title}
              </p>
            </div>

            {/* Value */}
            <div className="pl-0.5">
              {isAmount ? (
                <span className={`${cfg.value} font-extrabold text-base leading-none break-all`}>
                  {displayPriceWithSymbol(convertPrice(value))}
                </span>
              ) : (
                <span className={`${cfg.value} font-extrabold text-2xl leading-none`}>
                  {value}
                </span>
              )}
            </div>

            {showWithdrawButton && (
              <button
                onClick={handleWithdraw}
                className="mt-1 self-start px-3 py-1 text-[10px] font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-full transition-colors"
              >
                {translate("dashboard.stats.withdraw")}
              </button>
            )}
          </div>
        </div>

        {/* ── Desktop Card (≥ sm) — unchanged ── */}
        <div
          className={`hidden sm:flex bg-${color}-50 p-3 sm:p-4 rounded-lg border-2 border-${color}-200 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 min-h-[100px] flex-col`}
        >
          <div className='flex items-start flex-1'>
            <div className={`p-2 bg-${color}-100 rounded-full shadow-sm flex-shrink-0`}>
              {icon}
            </div>
            <div className={`${isRTL ? "mr-2 sm:mr-3" : "ml-2 sm:ml-3"} flex-1 min-w-0`}>
              <p className={`text-xs sm:text-sm font-semibold text-${color}-700 uppercase tracking-wide break-words leading-tight`}>
                {title}
              </p>
              <div className='mt-1 overflow-hidden'>
                {isAmount ? (
                  <span className={`text-${color}-700 font-bold text-sm sm:text-lg break-words`}>
                    {displayPriceWithSymbol(convertPrice(value))}
                  </span>
                ) : (
                  <span className='text-sm sm:text-lg font-bold text-gray-800 break-words'>
                    {value}
                  </span>
                )}
              </div>
              {showWithdrawButton && (
                <button
                  onClick={handleWithdraw}
                  className='mt-2 px-3 py-1.5 text-xs font-medium text-black bg-orange-500 hover:bg-orange-600 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2'
                >
                  {translate("dashboard.stats.withdraw")}
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  if (isLoading) {
    return (
      <div
        className='bg-card rounded-lg shadow-md'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Header */}
        <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]'>
          <div className='flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]'>

            {/* Left Side Grouping */}
            <div className='flex flex-col justify-center'>
              <h2 className='text-white text-base sm:text-lg font-semibold whitespace-nowrap'>
                {translate("dashboard.title")}
              </h2>
            </div>

            {/* Right Side Grouping (Kept for height and alignment consistency) */}
            <div className='flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto'>
              {/* Empty space that mirrors the search/filter area in Gate Pass */}
            </div>

          </div>
        </div>


        <div className='px-3 py-6'>
          <div className='animate-pulse'>
            <div className='h-4 bg-gray-200 rounded w-1/2 mb-6'></div>
            <div className='grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-6 mb-8'>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className='h-24 bg-gray-200 rounded-lg'></div>
              ))}
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
              {[1, 2, 3].map((i) => (
                <div key={i} className='h-24 bg-gray-200 rounded-lg'></div>
              ))}
            </div>
            <div className='space-y-3'>
              {[1, 2].map((i) => (
                <div key={i} className='h-20 bg-gray-200 rounded-lg'></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className='bg-card rounded-lg shadow-md'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className='bg-card font-medium rounded-t-[8px] sm:rounded-t-[16px]'>
        <div className='flex flex-col lg:flex-row lg:items-center justify-between px-4 sm:px-5 py-3 sm:py-4 rounded-t-[8px] sm:rounded-t-[12px] bg-primary-soft gap-4 min-h-[120px] lg:min-h-[72px]'>

          {/* Left Side Grouping */}
          <div className='flex flex-col justify-center'>
            <h2 className='text-white text-base sm:text-lg font-semibold whitespace-nowrap'>
              {translate("dashboard.title")}
            </h2>
          </div>

          {/* Right Side Grouping (Kept for height and alignment consistency) */}
          <div className='flex flex-col md:flex-row items-start md:items-center gap-3 ms-auto lg:ms-0 w-full lg:w-auto'>
            {/* Empty space that mirrors the search/filter area in Gate Pass */}
          </div>

        </div>
      </div>


      <div className='px-3 py-6'>
        {/* Stats Grid */}
        <div className='grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-6 mb-8'>
          <StatCard
            title={translate("dashboard.stats.totalVehicles")}
            value={apiData.totalSellerVehicles || 0}
            icon={
              <svg
                className='w-6 h-6 text-blue-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                />
              </svg>
            }
            color='blue'
          />
          <StatCard
            title={translate("dashboard.stats.soldVehicles")}
            value={apiData.soldSellerVehicles || 0}
            icon={
              <svg
                className='w-6 h-6 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            }
            color='green'
          />
          <StatCard
            title={translate("dashboard.stats.pendingVehicles")}
            value={apiData.pendingApprovalSellerVehicles || 0}
            icon={
              <svg
                className='w-6 h-6 text-orange-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            }
            color='orange'
          />
          <StatCard
            title={translate("dashboard.stats.unsoldVehicles")}
            value={apiData.unsoldSellerVehicles || 0}
            icon={
              <svg
                className='w-6 h-6 text-red-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                />
              </svg>
            }
            color='red'
          />
          <StatCard
            title={translate("dashboard.stats.successRate")}
            value={`${apiData.successRate || 0}%`}
            icon={
              <svg
                className='w-6 h-6 text-yellow-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                />
              </svg>
            }
            color='yellow'
          />
          <StatCard
            title={translate("dashboard.stats.totalRevenue")}
            value={apiData.totalEarnings || 0}
            icon={
              <svg
                width='17'
                height='17'
                viewBox='0 0 25 21'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                class='inline-block align-middle'
              >
                <path
                  d='M24.1035 9.97894L23.9211 9.81052C23.6263 9.52982 23.2754 9.38947 22.8965 9.38947H20.9316C20.9597 9.72631 20.9737 10.0632 20.9737 10.4281C20.9737 10.793 20.9597 11.1298 20.9316 11.4807H22.2649C23.2754 11.4807 24.1035 12.4351 24.1035 13.6281V14.1614L23.9211 13.9789C23.6263 13.7123 23.2754 13.5719 22.8965 13.5719H20.6369C19.5561 18.3018 15.7807 20.8702 9.82983 20.8702H2.19475C2.19475 20.8702 3.23335 20.0702 3.23335 17.3895V14.5719H1.95615C0.931594 14.5719 0.103516 13.6035 0.103516 12.4246V10.8912L0.300012 11.0596C0.580713 11.3263 0.931597 11.4807 1.31054 11.4807H3.23335V9.38947H1.95615C0.931594 9.38947 0.103516 8.42105 0.103516 7.2421V5.70877L0.300012 5.89123C0.580713 6.15789 0.931597 6.29824 1.31054 6.29824H3.23335V3.63509C3.23335 0.870176 2.19475 0 2.19475 0H9.82983C15.6123 0 19.486 2.54035 20.6228 7.29824H22.2649C23.2754 7.29824 24.1035 8.25263 24.1035 9.44561V9.97894ZM9.54912 1.0386H7.36317V6.29824H17.0719C16.3421 1.94737 13.8579 1.0386 9.54912 1.0386ZM17.3246 10.4281C17.3246 10.0632 17.3105 9.72631 17.2965 9.38947H7.36317V11.4807H17.2965C17.3105 11.1298 17.3246 10.793 17.3246 10.4281ZM7.36317 19.8175H9.5772C14.1386 19.7053 16.3842 18.5158 17.0719 14.5719H7.36317V16.6947V19.8175Z'
                  fill='#32CD32'
                ></path>
              </svg>
            }
            color='green'
            isAmount={true}
          />
          <StatCard
            title={translate("dashboard.stats.netEarningsAfterCommission")}
            value={apiData.netEarnings || 0}
            icon={
              <svg
                width='17'
                height='17'
                viewBox='0 0 25 21'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                class='inline-block align-middle'
              >
                <path
                  d='M24.1035 9.97894L23.9211 9.81052C23.6263 9.52982 23.2754 9.38947 22.8965 9.38947H20.9316C20.9597 9.72631 20.9737 10.0632 20.9737 10.4281C20.9737 10.793 20.9597 11.1298 20.9316 11.4807H22.2649C23.2754 11.4807 24.1035 12.4351 24.1035 13.6281V14.1614L23.9211 13.9789C23.6263 13.7123 23.2754 13.5719 22.8965 13.5719H20.6369C19.5561 18.3018 15.7807 20.8702 9.82983 20.8702H2.19475C2.19475 20.8702 3.23335 20.0702 3.23335 17.3895V14.5719H1.95615C0.931594 14.5719 0.103516 13.6035 0.103516 12.4246V10.8912L0.300012 11.0596C0.580713 11.3263 0.931597 11.4807 1.31054 11.4807H3.23335V9.38947H1.95615C0.931594 9.38947 0.103516 8.42105 0.103516 7.2421V5.70877L0.300012 5.89123C0.580713 6.15789 0.931597 6.29824 1.31054 6.29824H3.23335V3.63509C3.23335 0.870176 2.19475 0 2.19475 0H9.82983C15.6123 0 19.486 2.54035 20.6228 7.29824H22.2649C23.2754 7.29824 24.1035 8.25263 24.1035 9.44561V9.97894ZM9.54912 1.0386H7.36317V6.29824H17.0719C16.3421 1.94737 13.8579 1.0386 9.54912 1.0386ZM17.3246 10.4281C17.3246 10.0632 17.3105 9.72631 17.2965 9.38947H7.36317V11.4807H17.2965C17.3105 11.1298 17.3246 10.793 17.3246 10.4281ZM7.36317 19.8175H9.5772C14.1386 19.7053 16.3842 18.5158 17.0719 14.5719H7.36317V16.6947V19.8175Z'
                  fill='#DAA520'
                ></path>
              </svg>
            }
            color='yellow'
            isAmount={true}
          />
          <StatCard
            title={translate("dashboard.stats.totalCommissionWithVat")}
            value={apiData.totalCommissionWithVat || 0}
            icon={
              <svg
                width='17'
                height='17'
                viewBox='0 0 25 21'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                class='inline-block align-middle'
              >
                <path
                  d='M24.1035 9.97894L23.9211 9.81052C23.6263 9.52982 23.2754 9.38947 22.8965 9.38947H20.9316C20.9597 9.72631 20.9737 10.0632 20.9737 10.4281C20.9737 10.793 20.9597 11.1298 20.9316 11.4807H22.2649C23.2754 11.4807 24.1035 12.4351 24.1035 13.6281V14.1614L23.9211 13.9789C23.6263 13.7123 23.2754 13.5719 22.8965 13.5719H20.6369C19.5561 18.3018 15.7807 20.8702 9.82983 20.8702H2.19475C2.19475 20.8702 3.23335 20.0702 3.23335 17.3895V14.5719H1.95615C0.931594 14.5719 0.103516 13.6035 0.103516 12.4246V10.8912L0.300012 11.0596C0.580713 11.3263 0.931597 11.4807 1.31054 11.4807H3.23335V9.38947H1.95615C0.931594 9.38947 0.103516 8.42105 0.103516 7.2421V5.70877L0.300012 5.89123C0.580713 6.15789 0.931597 6.29824 1.31054 6.29824H3.23335V3.63509C3.23335 0.870176 2.19475 0 2.19475 0H9.82983C15.6123 0 19.486 2.54035 20.6228 7.29824H22.2649C23.2754 7.29824 24.1035 8.25263 24.1035 9.44561V9.97894ZM9.54912 1.0386H7.36317V6.29824H17.0719C16.3421 1.94737 13.8579 1.0386 9.54912 1.0386ZM17.3246 10.4281C17.3246 10.0632 17.3105 9.72631 17.2965 9.38947H7.36317V11.4807H17.2965C17.3105 11.1298 17.3246 10.793 17.3246 10.4281ZM7.36317 19.8175H9.5772C14.1386 19.7053 16.3842 18.5158 17.0719 14.5719H7.36317V16.6947V19.8175Z'
                  fill='#000000'
                ></path>
              </svg>
            }
            color='red'
            isAmount={true}
          />

          <StatCard
            title={translate("dashboard.stats.averageSalePrice")}
            value={apiData.averageEarning || 0}
            icon={
              <svg
                className='w-6 h-6 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z'
                />
              </svg>
            }
            color='green'
            isAmount={true}
          />

          <StatCard
            title={translate("dashboard.stats.totalBidsReceived")}
            value={apiData.totalBids || 0}
            icon={
              <svg
                className='w-6 h-6 text-pink-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 10V3L4 14h7v7l9-11h-7z'
                />
              </svg>
            }
            color='pink'
          />
          <StatCard
            title={translate("dashboard.stats.withdrawableAmount")}
            isAmount={true}
            value={apiData.withdrawAbleAmount || 0}
            icon={
              <svg
                className='w-6 h-6 text-pink-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 10V3L4 14h7v7l9-11h-7z'
                />
              </svg>
            }
            color='pink'
            showWithdrawButton={false}
          />
          <StatCard
            title={translate("dashboard.stats.withdrawnAmount")}
            isAmount={true}
            value={apiData?.withdrawnAmount}
            icon={
              <svg
                className='w-6 h-6 text-pink-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 10V3L4 14h7v7l9-11h-7z'
                />
              </svg>
            }
            color='red'
          />
        </div>

        {/* Recent Sales */}
        {/* <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Recent Sales
          </h2>
          <div className="bg-gray-50 rounded-lg p-6">
            <div className="space-y-4">
              {salesData.recentSales.map((transaction) => (
                <TransactionItem
                  key={transaction.id}
                  transaction={transaction}
                />
              ))}
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Sales;
